/**
 * Prepares third-party job descriptions for display inside a result card.
 *
 * Feed descriptions arrive as plain text, HTML, or light Markdown and routinely
 * carry apply links, tracking URLs and recruiter mailtos. Links and formatting
 * are removed: the card's own button is the only route to the posting, and the
 * expand panel should read as plain text with real line breaks — not a wall of
 * leftover markup.
 */

const HTML_ENTITIES = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&nbsp;': ' ',
};

/** TLDs common in job posts, only stripped when followed by a path. */
const BARE_DOMAIN = new RegExp(
  '\\b[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.(?:com|net|org|io|co|ai|jobs|careers|uk|us|ca|de|fr|nl|es|it|au|eu)(?:/[^\\s]*)',
  'gi'
);

const decodeEntities = (text) =>
  text.replace(
    /&(?:amp|lt|gt|quot|apos|nbsp|#39);/gi,
    (entity) => HTML_ENTITIES[entity.toLowerCase()] || entity
  );

/**
 * Turn structural HTML into newlines before tags are stripped, so <p>/<br>/<li>
 * still produce readable breaks instead of one long run-on line.
 */
export const htmlBreaksToNewlines = (text) =>
  text
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*hr\s*\/?\s*>/gi, '\n\n')
    .replace(
      /<\s*\/\s*(?:p|div|h[1-6]|li|tr|blockquote|section|article|pre)\s*>/gi,
      '\n'
    )
    .replace(/<\s*\/\s*(?:ul|ol|table)\s*>/gi, '\n\n')
    .replace(
      /<\s*(?:p|div|h[1-6]|li|tr|blockquote|section|article)\b[^>]*>/gi,
      '\n'
    );

/** Removes links of every flavour, keeping the surrounding copy readable. */
export const stripLinks = (text) =>
  text
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/(?:https?:\/\/|ftp:\/\/|www\.|mailto:)[^\s<>()[\]{}"']+/gi, '')
    .replace(BARE_DOMAIN, '');

/**
 * Strip common Markdown markers while keeping the words they wrapped.
 * List markers become a plain bullet so structure survives without raw `*`/`-`.
 */
export const stripMarkdown = (text) =>
  text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/gm, '')
    .replace(/^>\s?/gm, '')
    .replace(/```[\s\S]*?```/g, (block) =>
      block.replace(/```\w*\n?/g, '').trim()
    )
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/~~([^~]+)~~/g, '$1')
    .replace(/\*([^*\n]+)\*/g, '$1')
    .replace(/_([^_\n]+)_/g, '$1')
    .replace(/^[\t ]*[-*+]\s+/gm, '• ')
    .replace(/^[\t ]*\d+\.\s+/gm, '');

/** Tidies the gaps and orphaned punctuation that link removal leaves behind. */
const tidy = (text) =>
  text
    .replace(/\r\n?/g, '\n')
    .replace(/\(\s*\)|\[\s*\]|<\s*>/g, '')
    .replace(/[ \t]*([,;:])\s*(?=[,;:.])/g, '')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/[ \t]+$/gm, '')
    .replace(/^[ \t]*[-•*–]\s*$/gm, '')
    .replace(/^[ \t]*[,;:.]+[ \t]*$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

/**
 * Splits a description into renderable paragraphs with links and formatting
 * stripped. Single newlines inside a paragraph are preserved for display
 * (`white-space: pre-wrap` on the card).
 *
 * @param  {string} desc Raw description from the search feed.
 * @return {string[]}    Paragraphs, empty when there is nothing to show.
 */
export const getDescriptionParagraphs = (desc) => {
  if (!desc || typeof desc !== 'string') return [];

  const cleaned = tidy(
    stripMarkdown(stripLinks(htmlBreaksToNewlines(decodeEntities(desc))))
  );
  if (!cleaned) return [];

  return cleaned
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => /[a-z0-9]/i.test(paragraph));
};
